import { useEffect, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, query, where, orderBy, documentId, limit } from "firebase/firestore";
import { Plus, X, ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { db } from "../firebase-config";
import { DISABLE_DAILY_LIMIT } from "../config";
import { computeDailyStats } from "../dailyStatsLogic";
import { shareCardAsImage } from "../shareImage";
import DailyOutcomesCard from "./DailyOutcomesCard";
import ShopItemPreview from "./ShopItemPreview";
import { SUBTITLE_ICON_KEYS, SUBTITLE_ICONS } from "../icons";
import { TRUST_COLOR, BETRAY_COLOR, TEXT_DARK, TEXT_MUTED, BORDER } from "../theme";

function todayKey() {
  return new Date().toISOString().split('T')[0];
}

function addDaysToKey(dateKey, days) {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const BLANK_RULES = { cc: "", dd: "", cd_defector: "", cd_cooperator: "" };

function Admin({ onClose }) {
  const [tab, setTab] = useState("schedule");

  return (
    <div style={{ padding: 20, maxWidth: "640px", margin: "30px auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: "700" }}>admin</h2>
        <button onClick={onClose} style={{ padding: "6px 14px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}>
          close
        </button>
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
        <button
          onClick={() => setTab("schedule")}
          style={{ flex: 1, padding: "10px", background: tab === "schedule" ? TRUST_COLOR : "#fff", color: tab === "schedule" ? "#fff" : TEXT_DARK, border: `1px solid ${tab === "schedule" ? TRUST_COLOR : BORDER}`, borderRadius: "8px", fontWeight: "600", fontSize: "0.8125rem" }}
        >
          schedule
        </button>
        <button
          onClick={() => setTab("stats")}
          style={{ flex: 1, padding: "10px", background: tab === "stats" ? TRUST_COLOR : "#fff", color: tab === "stats" ? "#fff" : TEXT_DARK, border: `1px solid ${tab === "stats" ? TRUST_COLOR : BORDER}`, borderRadius: "8px", fontWeight: "600", fontSize: "0.8125rem" }}
        >
          stats
        </button>
        <button
          onClick={() => setTab("shop")}
          style={{ flex: 1, padding: "10px", background: tab === "shop" ? TRUST_COLOR : "#fff", color: tab === "shop" ? "#fff" : TEXT_DARK, border: `1px solid ${tab === "shop" ? TRUST_COLOR : BORDER}`, borderRadius: "8px", fontWeight: "600", fontSize: "0.8125rem" }}
        >
          shop
        </button>
      </div>

      {tab === "schedule" ? <ScheduleEditor /> : tab === "stats" ? <StatsViewer /> : <ShopManager />}
    </div>
  );
}

// Normally only future days are editable — today's config is meant to be
// settled before the day starts. While DISABLE_DAILY_LIMIT is on for
// testing, today is unlocked too, so changes can be tried out immediately.
const editFloor = DISABLE_DAILY_LIMIT ? todayKey() : addDaysToKey(todayKey(), 1);

function ScheduleEditor() {
  const [selectedDate, setSelectedDate] = useState(editFloor);
  const [rules, setRules] = useState(BLANK_RULES);
  const [hasQuestion, setHasQuestion] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [choices, setChoices] = useState(["", ""]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    loadDay(selectedDate);
    loadUpcoming();
  }, [selectedDate]);

  const loadDay = async (dateKey) => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "dailyConfig", dateKey));
      if (snap.exists()) {
        const data = snap.data();
        setRules(data.rules ? {
          cc: String(data.rules.cc), dd: String(data.rules.dd),
          cd_defector: String(data.rules.cd_defector), cd_cooperator: String(data.rules.cd_cooperator),
        } : BLANK_RULES);
        if (data.question) {
          setHasQuestion(true);
          setQuestionText(data.question.text || "");
          setChoices(data.question.choices && data.question.choices.length ? data.question.choices : ["", ""]);
        } else {
          setHasQuestion(false);
          setQuestionText("");
          setChoices(["", ""]);
        }
      } else {
        setRules(BLANK_RULES);
        setHasQuestion(false);
        setQuestionText("");
        setChoices(["", ""]);
      }
    } catch (error) {
      console.error("Error loading day config:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcoming = async () => {
    try {
      const q = query(
        collection(db, "dailyConfig"),
        orderBy(documentId()),
        where(documentId(), ">=", editFloor),
        limit(14)
      );
      const snapshot = await getDocs(q);
      setUpcoming(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error loading upcoming days:", error);
    }
  };

  const updateChoice = (i, value) => {
    setChoices((prev) => prev.map((c, idx) => (idx === i ? value : c)));
  };

  const addChoice = () => setChoices((prev) => [...prev, ""]);
  const removeChoice = (i) => setChoices((prev) => prev.filter((_, idx) => idx !== i));

  const isLocked = selectedDate < editFloor;

  const save = async () => {
    if (isLocked) return;
    const parsed = {
      cc: parseInt(rules.cc, 10),
      dd: parseInt(rules.dd, 10),
      cd_defector: parseInt(rules.cd_defector, 10),
      cd_cooperator: parseInt(rules.cd_cooperator, 10),
    };
    if (Object.values(parsed).some((v) => Number.isNaN(v))) {
      alert("fill in all four rule values");
      return;
    }

    const cleanedChoices = choices.map((c) => c.trim()).filter(Boolean);
    if (hasQuestion && (!questionText.trim() || cleanedChoices.length < 2)) {
      alert("a question needs text and at least 2 choices");
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, "dailyConfig", selectedDate), {
        rules: parsed,
        question: hasQuestion ? { text: questionText.trim(), choices: cleanedChoices } : null,
      });
      await loadUpcoming();
      alert(`saved ${selectedDate}`);
    } catch (error) {
      console.error("Error saving day config:", error);
      alert("failed to save — see console");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
        <button onClick={() => setSelectedDate((d) => { const prev = addDaysToKey(d, -1); return prev < editFloor ? editFloor : prev; })} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px" }}>
          <ChevronLeft size={16} color={TEXT_MUTED} />
        </button>
        <input
          type="date"
          value={selectedDate}
          min={editFloor}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{ flex: 1, padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.875rem" }}
        />
        <button onClick={() => setSelectedDate((d) => addDaysToKey(d, 1))} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px" }}>
          <ChevronRight size={16} color={TEXT_MUTED} />
        </button>
      </div>

      {isLocked && (
        <div style={{ padding: "10px", borderRadius: "8px", background: BETRAY_COLOR + "1a", color: BETRAY_COLOR, fontSize: "0.75rem", marginBottom: "14px", textAlign: "center" }}>
          {DISABLE_DAILY_LIMIT ? "this day has already passed and can't be edited" : "only future days can be edited — today and earlier are locked"}
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: "center", padding: "20px", color: TEXT_MUTED, fontSize: "0.8125rem" }}>loading...</p>
      ) : (
        <>
          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED, marginBottom: "8px" }}>payoff rules</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <RuleInput label="both trust" value={rules.cc} onChange={(v) => setRules((r) => ({ ...r, cc: v }))} disabled={isLocked} />
              <RuleInput label="both betray" value={rules.dd} onChange={(v) => setRules((r) => ({ ...r, dd: v }))} disabled={isLocked} />
              <RuleInput label="you betray them" value={rules.cd_defector} onChange={(v) => setRules((r) => ({ ...r, cd_defector: v }))} disabled={isLocked} />
              <RuleInput label="they betray you" value={rules.cd_cooperator} onChange={(v) => setRules((r) => ({ ...r, cd_cooperator: v }))} disabled={isLocked} />
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.8125rem", fontWeight: "600", marginBottom: "10px", cursor: isLocked ? "default" : "pointer" }}>
              <input type="checkbox" checked={hasQuestion} disabled={isLocked} onChange={(e) => setHasQuestion(e.target.checked)} />
              pre-game question
            </label>

            {hasQuestion && (
              <div style={{ paddingLeft: "4px" }}>
                <input
                  value={questionText}
                  disabled={isLocked}
                  onChange={(e) => setQuestionText(e.target.value)}
                  placeholder="e.g. which colour do you prefer?"
                  style={{ width: "100%", padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem", marginBottom: "8px" }}
                />
                {choices.map((choice, i) => (
                  <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                    <input
                      value={choice}
                      disabled={isLocked}
                      onChange={(e) => updateChoice(i, e.target.value)}
                      placeholder={`choice ${i + 1}`}
                      style={{ flex: 1, padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}
                    />
                    {choices.length > 2 && !isLocked && (
                      <button onClick={() => removeChoice(i)} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px" }}>
                        <X size={14} color={TEXT_MUTED} />
                      </button>
                    )}
                  </div>
                ))}
                {!isLocked && (
                  <button onClick={addChoice} style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: TRUST_COLOR, fontSize: "0.75rem", fontWeight: "600", padding: "4px" }}>
                    <Plus size={14} /> add choice
                  </button>
                )}
              </div>
            )}
          </div>

          <button
            onClick={save}
            disabled={isLocked || saving}
            style={{ padding: "12px", background: TRUST_COLOR, color: "#fff", border: "none", borderRadius: "10px", width: "100%", fontWeight: "700", fontSize: "0.875rem", marginBottom: "20px" }}
          >
            {saving ? "saving..." : `save ${selectedDate}`}
          </button>
        </>
      )}

      {upcoming.length > 0 && (
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED, marginBottom: "8px" }}>upcoming configured days</div>
          <div style={{ border: `1px solid ${BORDER}`, borderRadius: "10px", overflow: "hidden" }}>
            {upcoming.map((day, i) => (
              <div
                key={day.id}
                onClick={() => setSelectedDate(day.id)}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: i < upcoming.length - 1 ? `1px solid ${BORDER}` : "none", cursor: "pointer", background: day.id === selectedDate ? "#f3f5ff" : "#fff" }}
              >
                <span style={{ fontSize: "0.8125rem", fontWeight: "600" }}>{day.id}</span>
                <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>{day.question ? "has question" : "rules only"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RuleInput({ label, value, onChange, disabled }) {
  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px 10px" }}>
      <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginBottom: "4px" }}>{label}</div>
      <input
        type="number"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", border: "none", outline: "none", fontSize: "0.9375rem", fontWeight: "700" }}
      />
    </div>
  );
}

function StatsViewer() {
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    loadStats(selectedDate);
  }, [selectedDate]);

  const share = async () => {
    setSharing(true);
    const result = await shareCardAsImage(cardRef.current, {
      filename: `trust-game-stats-${selectedDate}.png`,
      title: "trust game — outcomes",
      text: `outcomes for ${selectedDate} on trust game`,
    });
    setSharing(false);
    if (!result.ok) alert(result.error);
    else if (result.method === "download") alert("image downloaded and link copied — ready to post!");
  };

  const loadStats = async (dateKey) => {
    setLoading(true);
    try {
      const [y, m, d] = dateKey.split('-').map(Number);
      const start = new Date(y, m - 1, d);
      const end = new Date(y, m - 1, d + 1);

      const gamesQuery = query(
        collection(db, "games"),
        where("state", "==", "results"),
        where("finishedAt", ">=", start),
        where("finishedAt", "<", end)
      );
      const snapshot = await getDocs(gamesQuery);
      setStats(computeDailyStats(snapshot.docs.map((d) => d.data())));
    } catch (error) {
      console.error("Error loading admin stats:", error);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
        <button onClick={() => setSelectedDate((d) => addDaysToKey(d, -1))} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px" }}>
          <ChevronLeft size={16} color={TEXT_MUTED} />
        </button>
        <input
          type="date"
          value={selectedDate}
          max={todayKey()}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{ flex: 1, padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.875rem" }}
        />
        <button onClick={() => setSelectedDate((d) => (d < todayKey() ? addDaysToKey(d, 1) : d))} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px" }}>
          <ChevronRight size={16} color={TEXT_MUTED} />
        </button>
      </div>

      <div ref={cardRef}>
        <DailyOutcomesCard stats={stats} loading={loading} dateLabel={selectedDate} />
      </div>

      {stats && stats.totalGames > 0 && (
        <button
          onClick={share}
          disabled={sharing}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", width: "100%", padding: "12px", marginTop: "16px", background: TRUST_COLOR, color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", fontSize: "0.875rem" }}
        >
          <Share2 size={16} />
          {sharing ? "generating image..." : "share as image"}
        </button>
      )}
    </div>
  );
}

const SHOP_TYPES = ["subtitle", "border", "bubble"];
const SHOP_TYPE_LABELS = { subtitle: "subtitle", border: "avatar border", bubble: "chat bubble theme" };
const BLANK_CONFIG = {
  subtitle: { text: "", color: "#4361ee", icon: "", italic: false },
  border: { colorFrom: "#4361ee", colorTo: "#f08c44", thickness: 3 },
  bubble: { background: "#4361ee", color: "#ffffff", radius: "rounded", bold: false },
};

// "2026-06-28T14:30" — what a <input type="datetime-local"> needs, in the
// browser's local timezone (toISOString would give UTC and shift the time).
function toDateTimeLocal(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function ShopManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [type, setType] = useState("subtitle");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [config, setConfig] = useState(BLANK_CONFIG.subtitle);
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "shopItems"));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error loading shop items:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setType("subtitle");
    setName("");
    setPrice("");
    setDescription("");
    setConfig(BLANK_CONFIG.subtitle);
    setExpiresAt("");
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setType(item.type);
    setName(item.name);
    setPrice(String(item.price));
    setDescription(item.description || "");
    setConfig({ ...BLANK_CONFIG[item.type], ...item.config });
    setExpiresAt(item.expiresAt?.toDate ? toDateTimeLocal(item.expiresAt.toDate()) : "");
  };

  const changeType = (newType) => {
    setType(newType);
    setConfig(BLANK_CONFIG[newType]);
  };

  const save = async () => {
    const priceNum = parseInt(price, 10);
    if (!name.trim() || Number.isNaN(priceNum) || priceNum < 0) {
      alert("name and a valid (non-negative) price are required");
      return;
    }
    if (type === "subtitle" && !config.text?.trim()) {
      alert("a subtitle needs some text");
      return;
    }

    setSaving(true);
    try {
      const expiresAtDate = expiresAt ? new Date(expiresAt) : null;
      if (editingId) {
        await updateDoc(doc(db, "shopItems", editingId), { type, name: name.trim(), price: priceNum, description: description.trim(), config, expiresAt: expiresAtDate });
      } else {
        await addDoc(collection(db, "shopItems"), { type, name: name.trim(), price: priceNum, description: description.trim(), config, expiresAt: expiresAtDate, active: true, createdAt: serverTimestamp() });
      }
      await loadItems();
      resetForm();
    } catch (error) {
      console.error("Error saving shop item:", error);
      alert("failed to save — see console");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await updateDoc(doc(db, "shopItems", item.id), { active: item.active === false });
      loadItems();
    } catch (error) {
      console.error("Error toggling shop item:", error);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`delete "${item.name}"? anyone who already owns it keeps it, but it'll disappear from the shop for everyone else.`)) return;
    try {
      await deleteDoc(doc(db, "shopItems", item.id));
      loadItems();
    } catch (error) {
      console.error("Error deleting shop item:", error);
    }
  };

  const previewItem = { type, config };

  return (
    <div>
      <div style={{ marginBottom: "20px" }}>
        <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED, marginBottom: "10px" }}>
          {editingId ? "edit item" : "new item"}
        </div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
          {SHOP_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => changeType(t)}
              style={{ flex: 1, padding: "8px", fontSize: "0.75rem", fontWeight: "600", background: type === t ? TRUST_COLOR : "#fff", color: type === t ? "#fff" : TEXT_DARK, border: `1px solid ${type === t ? TRUST_COLOR : BORDER}`, borderRadius: "8px" }}
            >
              {SHOP_TYPE_LABELS[t]}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="item name (shown in the shop)"
            style={{ flex: 1, padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}
          />
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="price"
            style={{ width: "90px", padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}
          />
        </div>

        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="description shown to players in the shop (optional)"
          style={{ width: "100%", padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem", marginBottom: "10px" }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span style={{ fontSize: "0.75rem", color: TEXT_MUTED, whiteSpace: "nowrap" }}>leaves the shop at</span>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            style={{ flex: 1, padding: "9px 10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}
          />
          {expiresAt && (
            <button onClick={() => setExpiresAt("")} style={{ padding: "9px 10px", fontSize: "0.6875rem", fontWeight: "600", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px" }}>
              clear
            </button>
          )}
        </div>

        {type === "subtitle" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                value={config.text || ""}
                onChange={(e) => setConfig((c) => ({ ...c, text: e.target.value }))}
                placeholder="subtitle text, e.g. trust legend"
                style={{ flex: 1, padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}
              />
              <input type="color" value={config.color || "#4361ee"} onChange={(e) => setConfig((c) => ({ ...c, color: e.target.value }))} style={{ width: "44px", border: `1px solid ${BORDER}`, borderRadius: "8px" }} />
            </div>
            <div>
              <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED, marginBottom: "6px" }}>icon (optional)</div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                <button
                  onClick={() => setConfig((c) => ({ ...c, icon: "" }))}
                  style={{ padding: "7px 10px", borderRadius: "8px", border: `1px solid ${!config.icon ? TRUST_COLOR : BORDER}`, background: !config.icon ? "#f3f5ff" : "#fff", fontSize: "0.6875rem", color: TEXT_MUTED, fontWeight: "600" }}
                >
                  none
                </button>
                {SUBTITLE_ICON_KEYS.map((key) => {
                  const IconComp = SUBTITLE_ICONS[key];
                  const selected = config.icon === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setConfig((c) => ({ ...c, icon: key }))}
                      title={key}
                      style={{ padding: "7px 9px", borderRadius: "8px", border: `1px solid ${selected ? TRUST_COLOR : BORDER}`, background: selected ? "#f3f5ff" : "#fff", display: "flex" }}
                    >
                      <IconComp size={14} color={config.color || TEXT_DARK} />
                    </button>
                  );
                })}
              </div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.75rem", color: TEXT_MUTED, cursor: "pointer" }}>
              <input type="checkbox" checked={!!config.italic} onChange={(e) => setConfig((c) => ({ ...c, italic: e.target.checked }))} />
              italic
            </label>
          </div>
        )}

        {type === "border" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>gradient</span>
              <input type="color" value={config.colorFrom || "#4361ee"} onChange={(e) => setConfig((c) => ({ ...c, colorFrom: e.target.value }))} style={{ width: "44px", border: `1px solid ${BORDER}`, borderRadius: "8px" }} />
              <input type="color" value={config.colorTo || "#f08c44"} onChange={(e) => setConfig((c) => ({ ...c, colorTo: e.target.value }))} style={{ width: "44px", border: `1px solid ${BORDER}`, borderRadius: "8px" }} />
              <span style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>(set both the same for a solid ring)</span>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>thickness</span>
              <input
                type="number"
                min="1"
                max="10"
                value={config.thickness ?? 3}
                onChange={(e) => setConfig((c) => ({ ...c, thickness: parseInt(e.target.value, 10) || 1 }))}
                style={{ width: "60px", padding: "6px 8px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}
              />
              <span style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>px</span>
            </div>
          </div>
        )}

        {type === "bubble" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>background</span>
              <input type="color" value={config.background || "#4361ee"} onChange={(e) => setConfig((c) => ({ ...c, background: e.target.value }))} style={{ width: "44px", border: `1px solid ${BORDER}`, borderRadius: "8px" }} />
              <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>text</span>
              <input type="color" value={config.color || "#ffffff"} onChange={(e) => setConfig((c) => ({ ...c, color: e.target.value }))} style={{ width: "44px", border: `1px solid ${BORDER}`, borderRadius: "8px" }} />
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>shape</span>
              <select
                value={config.radius || "rounded"}
                onChange={(e) => setConfig((c) => ({ ...c, radius: e.target.value }))}
                style={{ padding: "6px 8px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}
              >
                <option value="rounded">rounded</option>
                <option value="pill">pill</option>
                <option value="sharp">sharp</option>
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.75rem", color: TEXT_MUTED, cursor: "pointer" }}>
                <input type="checkbox" checked={!!config.bold} onChange={(e) => setConfig((c) => ({ ...c, bold: e.target.checked }))} />
                bold text
              </label>
            </div>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "10px", border: `1px solid ${BORDER}`, borderRadius: "8px", marginBottom: "12px" }}>
          <span style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>preview</span>
          <ShopItemPreview item={previewItem} photoURL={null} />
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={save} disabled={saving} style={{ flex: 1, padding: "12px", background: TRUST_COLOR, color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", fontSize: "0.875rem" }}>
            {saving ? "saving..." : editingId ? "save changes" : "add item"}
          </button>
          {editingId && (
            <button onClick={resetForm} style={{ padding: "12px 16px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "10px", fontWeight: "600", fontSize: "0.875rem" }}>
              cancel
            </button>
          )}
        </div>
      </div>

      <div style={{ fontSize: "0.75rem", fontWeight: "600", color: TEXT_MUTED, marginBottom: "8px" }}>catalogue</div>
      {loading ? (
        <p style={{ textAlign: "center", padding: "20px", color: TEXT_MUTED, fontSize: "0.8125rem" }}>loading...</p>
      ) : items.length === 0 ? (
        <p style={{ textAlign: "center", padding: "20px", color: TEXT_MUTED, fontSize: "0.8125rem" }}>no shop items yet — add one above.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {items.map((item) => (
            <div key={item.id} style={{ border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "10px 14px", display: "flex", alignItems: "center", gap: "12px", background: "#fff", opacity: item.active === false ? 0.5 : 1 }}>
              <ShopItemPreview item={item} photoURL={null} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.8125rem", fontWeight: "700" }}>{item.name}</div>
                <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>
                  {SHOP_TYPE_LABELS[item.type]} · {item.price} pts
                  {item.expiresAt?.toDate ? ` · expires ${item.expiresAt.toDate().toLocaleString()}` : ""}
                  {item.active === false ? " · hidden" : ""}
                </div>
              </div>
              <button onClick={() => startEdit(item)} style={{ padding: "6px 10px", fontSize: "0.6875rem", fontWeight: "600", background: "#fff", color: TEXT_DARK, border: `1px solid ${BORDER}`, borderRadius: "6px" }}>
                edit
              </button>
              <button onClick={() => toggleActive(item)} style={{ padding: "6px 10px", fontSize: "0.6875rem", fontWeight: "600", background: "#fff", color: TEXT_DARK, border: `1px solid ${BORDER}`, borderRadius: "6px" }}>
                {item.active === false ? "unhide" : "hide"}
              </button>
              <button onClick={() => remove(item)} style={{ padding: "6px 10px", fontSize: "0.6875rem", fontWeight: "600", background: "#fff", color: BETRAY_COLOR, border: `1px solid ${BORDER}`, borderRadius: "6px" }}>
                delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Admin;
